import React, { useMemo } from "react";
import { Control, Controller, FieldErrors, UseFormRegister } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { RotateCcw } from "lucide-react";
import { FormCard } from "@/components/Core/FormCard";
import AppInputNew from "@/components/Core/AppInput/AppInputNew";
import AppSelect, { AppSelectController } from "@/components/Core/AppSelect/AppSelect";
import { CheckboxNewController } from "@/components/Core/Checkbox/CheckboxNew";
import { SegmentedControl } from "@/components/Core/SegmentedControl";
import { FormLabel } from "@/components/Core/AppText/AppText";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import {
	CustomFieldDefinition,
	CustomFieldEntity,
	CustomFieldPlacement,
	CustomFieldValues,
} from "@/models/Organisation";
import {
	CUSTOM_FIELDS_FORM_KEY,
	InheritedCustomFields,
	OVERRIDE_NO,
	OVERRIDE_YES,
	filterCustomFieldsByPlacement,
	formatCustomFieldValue,
	isGroupOwnedCustomField,
	isInheritableCustomField,
	overrideBooleanSegment,
	overrideSegmentToValue,
} from "./customFields.utils";

export interface CustomFieldsSectionProps {
	/** The form's entity — decides how `placement` is matched (job forms place inherited customer fields by `jobPlacement`). */
	entity: CustomFieldEntity;
	/** The host card: only definitions placed here are rendered (unless `ignorePlacement`). */
	placement?: CustomFieldPlacement;
	/**
	 * Render every definition in `definitions`, in the given order, regardless
	 * of `placement` — for hosts without cards (the koncern form).
	 */
	ignorePlacement?: boolean;
	/**
	 * Every active definition the host form works with (see
	 * `useCustomFieldDefinitions`; job forms pass `jobFormCustomFieldDefinitions`).
	 * The section slices it by `placement` and keeps the given order.
	 */
	definitions: CustomFieldDefinition[];
	/** react-hook-form wiring from the host form. Values live under `customFields.<key>`. */
	control: Control<any>;
	register: UseFormRegister<any>;
	errors?: FieldErrors<any>;
	/**
	 * Job forms: the customer's values for its inheritable fields. Customer
	 * definitions in `definitions` then render in inheritable mode — read-only
	 * for `show`, tri-state (inherit / own value) for `override`.
	 */
	inherited?: InheritedCustomFields;
	/**
	 * "card" wraps the fields in their own FormCard (the stand-alone "Övriga
	 * uppgifter" card); "inline" renders the rows only, for use inside an
	 * existing host card after its built-in inputs. Defaults to "card".
	 */
	variant?: "card" | "inline";
	/** Forwarded to the host's error-scroll refs (the card `<section>`); card variant only. */
	cardRef?: React.Ref<HTMLElement>;
	/**
	 * Extra classes on the card (card variant) or the row container (inline).
	 * Inline hosts that lay their own inputs out in a grid pass `"contents"` so
	 * the rows become cells of that grid instead of a stacked block below it.
	 */
	className?: string;
	/** Disable every control (e.g. while the host's fieldset is locked). */
	disabled?: boolean;
	/** Card title (Swedish, untranslated); card variant only. Defaults to "Övriga uppgifter". */
	title?: string;
	/**
	 * Customer forms: the customer belongs to a koncern. Group-owned definitions
	 * then render read-only ("Ägs av koncern") instead of as inputs.
	 */
	corporateGroupMember?: boolean;
	/**
	 * The koncern's values for its group-owned fields (shown read-only for a
	 * member). Leave undefined while unknown — e.g. a koncern just picked on
	 * the create page — and the fields read "Sätts av koncernen".
	 */
	corporateGroupValues?: CustomFieldValues;
}

type FieldError = { message?: string } | undefined;

/**
 * The org-defined custom fields of one host card: one control per definition
 * placed there. Renders nothing when no definition is placed on the card, so
 * host forms are unchanged for organisations without custom fields. Text,
 * number and date fields are plain inputs, selects a Select over the
 * definition's options, booleans a checkbox; labels and descriptions come
 * straight from the definition (Swedish, untranslated).
 *
 * On a job form, customer definitions with `inheritance` render against the
 * customer's value: `show` as a read-only "Ärvd från kund" display, `override`
 * as a tri-state control where "inherit" means the key is left out of the
 * payload (see `buildCustomFieldsPayload`).
 *
 * On a customer form of a koncern member (`corporateGroupMember`), group-owned
 * definitions render as a read-only "Ägs av koncern" display of the koncern's
 * value; non-members get the normal input.
 */
const CustomFieldsSection: React.FC<CustomFieldsSectionProps> = ({
	entity,
	placement,
	definitions,
	control,
	register,
	errors,
	inherited,
	variant = "card",
	cardRef,
	className,
	disabled,
	title = "Övriga uppgifter",
	ignorePlacement,
	corporateGroupMember,
	corporateGroupValues,
}) => {
	const placed = useMemo(
		() =>
			ignorePlacement || !placement
				? definitions
				: filterCustomFieldsByPlacement(definitions, placement, entity),
		[definitions, placement, entity, ignorePlacement]
	);
	if (placed.length === 0) return null;

	const fieldErrors = (errors?.[CUSTOM_FIELDS_FORM_KEY] ?? {}) as Record<string, FieldError>;

	const renderField = (definition: CustomFieldDefinition) => {
		const name = `${CUSTOM_FIELDS_FORM_KEY}.${definition.key}`;
		const common = {
			definition,
			name,
			control,
			disabled,
			error: fieldErrors[definition.key]?.message,
		};
		if (entity === "job" && isInheritableCustomField(definition)) {
			const inheritedValue = inherited?.[definition.key]?.value;
			if (definition.inheritance === "show") {
				return <InheritedShowField definition={definition} inheritedValue={inheritedValue} />;
			}
			if (definition.type === "boolean") {
				return <OverrideBooleanField {...common} inheritedValue={inheritedValue} />;
			}
			return <OverrideInputField {...common} inheritedValue={inheritedValue} />;
		}
		if (entity === "customer" && corporateGroupMember && isGroupOwnedCustomField(definition)) {
			return (
				<GroupOwnedField
					definition={definition}
					known={corporateGroupValues !== undefined}
					groupValue={corporateGroupValues?.[definition.key]}
				/>
			);
		}
		return <StandardField {...common} register={register} />;
	};

	if (variant === "inline") {
		return (
			<div className={cn("mt-4 flex flex-col gap-4", className)}>
				{placed.map((definition) => (
					<div key={definition.key}>{renderField(definition)}</div>
				))}
			</div>
		);
	}

	return (
		<FormCard ref={cardRef} title={title} skipTranslation className={className}>
			{/* One field per row, same spacing as the built-in fields of the other cards. */}
			<div className="flex flex-col gap-4">
				{placed.map((definition) => (
					<div key={definition.key}>{renderField(definition)}</div>
				))}
			</div>
		</FormCard>
	);
};

export default CustomFieldsSection;

// ---------------------------------------------------------------------------
// Field renderers
// ---------------------------------------------------------------------------

interface FieldProps {
	definition: CustomFieldDefinition;
	name: string;
	control: Control<any>;
	disabled?: boolean;
	error?: string;
}

/** The input for a field on its own entity's form (and for plain job fields). */
const StandardField: React.FC<FieldProps & { register: UseFormRegister<any> }> = ({
	definition,
	name,
	control,
	register,
	disabled,
	error,
}) => {
	const { t } = useTranslation();
	const description = definition.description?.sv;

	if (definition.type === "boolean") {
		return (
			<CheckboxNewController
				control={control}
				field={name}
				label={definition.label.sv}
				hint={description}
				skipTranslation
				disabled={disabled}
			/>
		);
	}

	if (definition.type === "select") {
		return (
			<div className="flex flex-col">
				<AppSelectController<{ id: string; name: string }>
					control={control}
					name={name}
					label={definition.label.sv}
					mandatory={!!definition.required}
					options={selectOptions(definition, t("customFields.noValue"))}
					getOptionLabel={(option) => option.name}
					getOptionValue={(option) => option.id}
					placeholder="customFields.selectPlaceholder"
					errors={error}
					disabled={disabled}
					margin="0"
				/>
				{description && <FieldHint>{description}</FieldHint>}
			</div>
		);
	}

	return (
		<AppInputNew
			{...register(name)}
			label={definition.label.sv}
			skipLabelTranslation
			mandatory={!!definition.required}
			description={description}
			type={inputType(definition)}
			step={definition.type === "number" ? "any" : undefined}
			errors={error}
			disabled={disabled}
		/>
	);
};

/** A `show` customer field on the job form: the customer's value, read-only. */
const InheritedShowField: React.FC<{
	definition: CustomFieldDefinition;
	inheritedValue: InheritedCustomFields[string]["value"] | undefined;
}> = ({ definition, inheritedValue }) => {
	const { t } = useTranslation();
	const text = formatCustomFieldValue(definition, inheritedValue);
	return (
		<div className="flex flex-col">
			<FormLabel>{definition.label.sv}</FormLabel>
			<div
				className={cn(
					"flex h-[2.3125rem] items-center rounded-sm border border-solid border-gray-200 bg-gray-50 px-3 text-[0.9375rem]",
					text ? "text-gray-700" : "text-gray-400"
				)}
			>
				{text || "—"}
			</div>
			<FieldHint>
				{text ? t("customFields.inheritedFromCustomer") : t("customFields.noCustomerValue")}
				{definition.description?.sv ? ` · ${definition.description.sv}` : ""}
			</FieldHint>
		</div>
	);
};

/** A group-owned field on a koncern member's customer form: the koncern's value, read-only. */
const GroupOwnedField: React.FC<{
	definition: CustomFieldDefinition;
	/** False while the koncern's values are unknown (just picked, not saved). */
	known: boolean;
	groupValue: CustomFieldValues[string] | undefined;
}> = ({ definition, known, groupValue }) => {
	const { t } = useTranslation();
	const text = known ? formatCustomFieldValue(definition, groupValue) : "";
	return (
		<div className="flex flex-col">
			<FormLabel>{definition.label.sv}</FormLabel>
			<div
				className={cn(
					"flex h-[2.3125rem] items-center rounded-sm border border-solid border-gray-200 bg-gray-50 px-3 text-[0.9375rem]",
					text ? "text-gray-700" : "text-gray-400"
				)}
			>
				{text || (known ? "—" : t("customFields.setByCorporateGroup"))}
			</div>
			<FieldHint>
				{known && !text
					? t("customFields.noCorporateGroupValue")
					: t("customFields.ownedByCorporateGroup")}
				{definition.description?.sv ? ` · ${definition.description.sv}` : ""}
			</FieldHint>
		</div>
	);
};

/**
 * An `override` boolean on the job form: Ja | Nej, preselected with the
 * customer's value while the job inherits (form value `undefined`, key left
 * out of the payload). Picking a segment stores the job's own value;
 * "Återställ till ärvd" drops it again.
 */
const OverrideBooleanField: React.FC<
	FieldProps & { inheritedValue: InheritedCustomFields[string]["value"] | undefined }
> = ({ definition, name, control, disabled, inheritedValue }) => {
	const { t } = useTranslation();
	return (
		<Controller
			control={control}
			name={name}
			render={({ field }) => {
				const overridden = typeof field.value === "boolean";
				return (
					<div className="flex flex-col">
						<SegmentedControl
							label={definition.label.sv}
							skipTranslation
							size="sm"
							disabled={disabled}
							value={overrideBooleanSegment(field.value, inheritedValue)}
							onChange={(segment) => field.onChange(overrideSegmentToValue(segment))}
							options={[
								{ value: OVERRIDE_YES, label: t("YES") },
								{ value: OVERRIDE_NO, label: t("NO") },
							]}
						/>
						<div className="flex items-start justify-between gap-3">
							<InheritedHint definition={definition} inheritedValue={inheritedValue} />
							{overridden && !disabled && (
								<button
									type="button"
									onClick={() => field.onChange(undefined)}
									className="mt-1.5 inline-flex shrink-0 items-center gap-1 rounded-sm border border-transparent bg-transparent px-1 text-xs font-medium text-action hover:underline"
								>
									<RotateCcw size={12} />
									{t("customFields.resetToInherited")}
								</button>
							)}
						</div>
					</div>
				);
			}}
		/>
	);
};

/**
 * An `override` text/number/date/select on the job form: empty = inherit
 * (the customer's value shows as placeholder / the "Ärv från kund" option),
 * anything typed is the job's own value, "Återställ till ärvd" empties it.
 */
const OverrideInputField: React.FC<
	FieldProps & { inheritedValue: InheritedCustomFields[string]["value"] | undefined }
> = ({ definition, name, control, disabled, error, inheritedValue }) => {
	const { t } = useTranslation();
	const inheritedText = formatCustomFieldValue(definition, inheritedValue);
	const inputId = `custom-field-${definition.key}`;

	return (
		<Controller
			control={control}
			name={name}
			render={({ field }) => {
				const overridden = field.value !== undefined && field.value !== "";
				return (
					<div className="flex flex-col">
						<FormLabel>
							{definition.label.sv}
							{definition.required ? " *" : ""}
						</FormLabel>
						{definition.type === "select" ? (
							<AppSelect<{ id: string; name: string }>
								name={field.name}
								value={field.value ?? ""}
								onChange={field.onChange}
								options={selectOptions(
									definition,
									inheritedText
										? `${t("customFields.inheritFromCustomer")} (${inheritedText})`
										: t("customFields.inheritFromCustomer")
								)}
								getOptionLabel={(option) => option.name}
								getOptionValue={(option) => option.id}
								errors={error}
								disabled={disabled}
								margin="0"
							/>
						) : (
							// The native input: the inherited value is the placeholder, and it
							// must not pass through i18n like AppInputNew's placeholder does.
							<Input
								id={inputId}
								name={field.name}
								ref={field.ref}
								value={field.value ?? ""}
								onChange={(event) => field.onChange(event.target.value)}
								onBlur={field.onBlur}
								type={inputType(definition)}
								step={definition.type === "number" ? "any" : undefined}
								placeholder={inheritedText || undefined}
								disabled={disabled}
								className={cn(
									"h-[2.3125rem] rounded-sm border-solid bg-white text-[0.9375rem] shadow-none focus-visible:border-blue-900 focus-visible:ring-0",
									error && "border-red-500 bg-red-50"
								)}
							/>
						)}
						<div className="flex items-start justify-between gap-3">
							<div className="flex flex-col">
								<InheritedHint definition={definition} inheritedValue={inheritedValue} />
								{error && <p className="mb-0 mt-1 text-xs text-danger">{t(error as any)}</p>}
							</div>
							{overridden && !disabled && (
								<button
									type="button"
									onClick={() => field.onChange("")}
									className="mt-1.5 inline-flex shrink-0 items-center gap-1 rounded-sm border border-transparent bg-transparent px-1 text-xs font-medium text-action hover:underline"
								>
									<RotateCcw size={12} />
									{t("customFields.resetToInherited")}
								</button>
							)}
						</div>
					</div>
				);
			}}
		/>
	);
};

/** "Ärvd från kund: <value>" (or "Inget värde på kunden") plus the definition's description. */
const InheritedHint: React.FC<{
	definition: CustomFieldDefinition;
	inheritedValue: InheritedCustomFields[string]["value"] | undefined;
}> = ({ definition, inheritedValue }) => {
	const { t } = useTranslation();
	const text = formatCustomFieldValue(definition, inheritedValue);
	return (
		<FieldHint>
			{text
				? t("customFields.inheritedFromCustomerValue", { value: text })
				: t("customFields.noCustomerValue")}
			{definition.description?.sv ? ` · ${definition.description.sv}` : ""}
		</FieldHint>
	);
};

const FieldHint: React.FC<{ children: React.ReactNode }> = ({ children }) => (
	<p className="mb-0 ml-0.5 mt-1.5 text-xs text-gray-500">{children}</p>
);

/** Native input type for a non-boolean, non-select definition. */
const inputType = (definition: CustomFieldDefinition) => {
	if (definition.type === "number") return "number";
	if (definition.type === "date") return "date";
	return "text";
};

/** Select options with a leading empty choice (AppSelect maps `id: ""` through its sentinel). */
const selectOptions = (definition: CustomFieldDefinition, emptyLabel: string) => [
	{ id: "", name: emptyLabel },
	...(definition.options ?? []).map((option) => ({ id: option.key, name: option.label.sv })),
];
